/**
 * MeetingWidgetsOverlay — rechtsonder in Jitsi (boven sessietimer)
 * - "Volgende meeting" (compact / groot) via meetingSettings
 * - RSVP Ja/Nee tijdens Afsluiting-blok (type commitment of label bevat "Afsluiting")
 */

import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  doc,
  setDoc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useStructuredGrowth } from './useStructuredGrowth';
import { useSessionFlow } from './useSessionFlow';
import type { SessionBlock, StructuredGrowthEntityType } from './types';

const DEFAULT_BLOCKS: SessionBlock[] = [
  { id: 'b1', type: 'intro', label: 'Introductie', durationMinutes: 5, order: 0 },
  { id: 'b5', type: 'commitment', label: 'Afsluiting', durationMinutes: 5, order: 4 },
];

function toEventStartMs(ev: any): number {
  try {
    if (ev?.eventDate instanceof Date) return ev.eventDate.getTime();
    if (ev?.eventDate?.toDate) return ev.eventDate.toDate().getTime();
    if (ev?.startTime && ev?.eventDate) {
      const d = ev.eventDate?.toDate ? ev.eventDate.toDate() : new Date(ev.eventDate);
      return d.getTime();
    }
    const d = ev?.startDateTime?.toDate ? ev.startDateTime.toDate() : new Date(ev?.startDateTime || 0);
    return d.getTime();
  } catch {
    return 0;
  }
}

function formatEventWhen(ev: any): { line1: string; line2: string } {
  const ms = toEventStartMs(ev);
  const d = new Date(ms || Date.now());
  const dateStr = d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
  const timeStr = ev?.startTime || d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  return { line1: ev?.title || 'Bijeenkomst', line2: `${dateStr} · ${timeStr}` };
}

function isClosingBlock(block: SessionBlock | null): boolean {
  if (!block) return false;
  if (block.type === 'commitment') return true;
  return /afsluiting/i.test(block.label || '');
}

interface MeetingWidgetsOverlayProps {
  entityId: string;
  entityType?: StructuredGrowthEntityType;
  meetingId: string;
  clubId?: string | null;
  clubName?: string;
  userId?: string | null;
  userDisplayName?: string;
}

const MeetingWidgetsOverlay: React.FC<MeetingWidgetsOverlayProps> = memo(function MeetingWidgetsOverlay({
  entityId,
  entityType = 'mastermind',
  meetingId,
  clubId,
  clubName,
  userId,
  userDisplayName = 'Lid',
}) {
  const { settings } = useStructuredGrowth(entityId, entityType);
  const blocks = (settings?.sessionStructure ?? []).length > 0 ? settings!.sessionStructure : DEFAULT_BLOCKS;
  const { currentBlock, flow } = useSessionFlow(entityId, meetingId, blocks, { autoAdvance: false, canControl: false });

  const [nextMeetingEnabled, setNextMeetingEnabled] = useState(true);
  const [nextMeetingSize, setNextMeetingSize] = useState<'compact' | 'large'>('compact');
  const [rsvpClosingEnabled, setRsvpClosingEnabled] = useState(false);
  const [nextEvent, setNextEvent] = useState<any | null>(null);
  const [myRsvp, setMyRsvp] = useState<'yes' | 'no' | null>(null);
  const [rsvpBusy, setRsvpBusy] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDecline, setShowDecline] = useState(false);

  useEffect(() => {
    if (!entityId) return;
    const unsub = onSnapshot(doc(db, 'meetingSettings', entityId), (snap) => {
      if (!snap.exists()) return;
      const d = snap.data() as any;
      if (typeof d.widgetNextMeetingEnabled === 'boolean') setNextMeetingEnabled(d.widgetNextMeetingEnabled);
      if (d.widgetNextMeetingSize === 'large' || d.widgetNextMeetingSize === 'compact') {
        setNextMeetingSize(d.widgetNextMeetingSize);
      }
      if (typeof d.widgetRsvpClosingEnabled === 'boolean') setRsvpClosingEnabled(d.widgetRsvpClosingEnabled);
    });
    return () => unsub();
  }, [entityId]);

  useEffect(() => {
    if (!entityId) return;
    const q = query(collection(db, 'clubEvents'), where('entityId', '==', entityId));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const now = Date.now();
      const future = list
        .filter((ev: any) => toEventStartMs(ev) >= now - 60_000)
        .sort((a: any, b: any) => toEventStartMs(a) - toEventStartMs(b));
      setNextEvent(future[0] || null);
    });
    return () => unsub();
  }, [entityId]);

  useEffect(() => {
    if (!userId || !nextEvent?.id) {
      setMyRsvp(null);
      return;
    }
    const ref = doc(db, 'clubEventRsvps', `${nextEvent.id}_${userId}`);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) {
        setMyRsvp(null);
        return;
      }
      const r = snap.data()?.response;
      setMyRsvp(r === 'yes' || r === 'no' ? r : null);
    });
    return () => unsub();
  }, [userId, nextEvent?.id]);

  const closing = flow.isRunning && isClosingBlock(currentBlock);
  const showNext = nextMeetingEnabled && nextEvent;
  const showRsvp = rsvpClosingEnabled && closing && nextEvent && userId;

  const saveRsvp = useCallback(
    async (response: 'yes' | 'no') => {
      if (!userId || !nextEvent?.id) return;
      if (response === 'no' && !declineReason.trim()) {
        setShowDecline(true);
        return;
      }
      setRsvpBusy(true);
      try {
        const when = formatEventWhen(nextEvent);
        const eventLabel = `${when.line1} — ${when.line2}`;

        await setDoc(
          doc(db, 'clubEventRsvps', `${nextEvent.id}_${userId}`),
          {
            eventId: nextEvent.id,
            entityId,
            clubId: clubId || null,
            userId,
            userName: userDisplayName,
            response,
            reason: response === 'no' ? declineReason.trim() || '—' : null,
            source: 'meeting_widget',
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        // ── Inbox: bevestiging voor de gebruiker zelf ──────────────────
        await addDoc(collection(db, 'inboxItems'), {
          userId,
          type: 'rsvp_confirmed',
          category: 'meeting',
          title: response === 'yes' ? '✅ Aanwezig bij volgende meeting' : 'Je hebt je afgemeld',
          message: response === 'yes'
            ? `Je bent aangemeld voor: ${eventLabel}`
            : `Je hebt je afgemeld voor: ${eventLabel}${declineReason.trim() ? ` (${declineReason.trim()})` : ''}`,
          entityId,
          eventId: nextEvent.id,
          isRead: false,
          createdAt: serverTimestamp(),
        });

        // ── Inbox: notificatie voor organisatoren/admins ───────────────
        try {
          const orgQuery = query(
            collection(db, 'entityMembers'),
            where('entityId', '==', entityId),
            where('role', 'in', ['owner', 'organizer'])
          );
          const orgSnap = await getDocs(orgQuery);
          for (const orgDoc of orgSnap.docs) {
            const orgData = orgDoc.data();
            if (orgData.userId === userId) continue; // geen dubbel bericht als organizer zelf RSVP'ed
            await addDoc(collection(db, 'inboxItems'), {
              userId: orgData.userId,
              type: 'rsvp_received',
              category: 'meeting',
              title: response === 'yes'
                ? `${userDisplayName} is aanwezig bij de volgende meeting`
                : `${userDisplayName} heeft zich afgemeld`,
              message: response === 'yes'
                ? `${userDisplayName} heeft "Ja" geantwoord voor: ${eventLabel}`
                : `${userDisplayName} heeft zich afgemeld voor: ${eventLabel}${declineReason.trim() ? ` — "${declineReason.trim()}"` : ''}`,
              entityId,
              eventId: nextEvent.id,
              fromUserId: userId,
              fromUserName: userDisplayName,
              isRead: false,
              createdAt: serverTimestamp(),
            });
          }
        } catch (orgErr) {
          console.warn('[MeetingWidgets] Organizer inbox notification failed', orgErr);
        }

        setMyRsvp(response);
        setShowDecline(false);
        setDeclineReason('');
      } catch (e) {
        console.error('[MeetingWidgets] RSVP save failed', e);
      } finally {
        setRsvpBusy(false);
      }
    },
    [userId, nextEvent, entityId, clubId, userDisplayName, declineReason]
  );

  const when = useMemo(() => (nextEvent ? formatEventWhen(nextEvent) : null), [nextEvent]);

  if (!showNext && !showRsvp) return null;

  return (
    <div className="flex flex-col items-start gap-2 pointer-events-none select-none max-w-[280px]">
      {showNext && when && (
        <div
          className={`pointer-events-none rounded-2xl border border-white/25 bg-black/55 backdrop-blur-md text-white shadow-xl ${
            nextMeetingSize === 'large' ? 'px-4 py-3' : 'px-3 py-2'
          }`}
          style={{ animation: 'mwFadeIn 0.35s ease-out both' }}
        >
          <p className="text-[9px] font-bold uppercase tracking-wider text-white/50 mb-0.5">Volgende meeting</p>
          <p className={`font-bold leading-tight ${nextMeetingSize === 'large' ? 'text-sm' : 'text-xs'}`}>{when.line1}</p>
          <p className={`text-white/80 ${nextMeetingSize === 'large' ? 'text-xs mt-1' : 'text-[10px] mt-0.5'}`}>{when.line2}</p>
          {clubName && nextMeetingSize === 'large' && (
            <p className="text-[10px] text-white/45 mt-1">{clubName}</p>
          )}
        </div>
      )}

      {showRsvp && (
        <div
          className="pointer-events-auto rounded-2xl border-2 border-emerald-400/50 bg-gray-950/90 backdrop-blur-md p-3 shadow-2xl w-full max-w-[260px]"
          style={{ animation: 'mwFadeIn 0.35s ease-out both' }}
        >
          <p className="text-[11px] font-bold text-white mb-1">Ben je er bij de volgende meeting?</p>
          {when && <p className="text-[10px] text-white/60 mb-2">{when.line2}</p>}
          {myRsvp ? (
            <p className="text-sm font-semibold text-emerald-400">
              {myRsvp === 'yes' ? '✓ Je bent aangemeld als aanwezig' : 'Je hebt aangegeven niet aanwezig te zijn'}
            </p>
          ) : showDecline ? (
            <div className="space-y-2">
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Korte reden (verplicht)"
                className="w-full text-xs text-gray-900 rounded-lg px-2 py-1.5 border border-gray-300"
                rows={2}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={rsvpBusy || !declineReason.trim()}
                  onClick={() => saveRsvp('no')}
                  className="flex-1 py-2 rounded-lg bg-red-600 text-white text-xs font-bold disabled:opacity-40"
                >
                  Verstuur
                </button>
                <button
                  type="button"
                  onClick={() => { setShowDecline(false); setDeclineReason(''); }}
                  className="px-3 py-2 rounded-lg bg-white/10 text-white text-xs"
                >
                  Terug
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 mt-1">
              <button
                type="button"
                disabled={rsvpBusy}
                onClick={() => saveRsvp('yes')}
                className="flex-1 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-bold shadow-lg"
              >
                Ja
              </button>
              <button
                type="button"
                disabled={rsvpBusy}
                onClick={() => setShowDecline(true)}
                className="flex-1 py-2.5 rounded-xl bg-white/15 hover:bg-white/25 text-white text-xs font-bold"
              >
                Nee
              </button>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes mwFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
});

export default MeetingWidgetsOverlay;
